import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CloudRain, ShieldCheck, FileText, Users } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
            <div className="flex items-center gap-3 sm:gap-4">
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-sm font-medium text-slate-600 hover:text-slate-950 transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/register"
                data-testid="nav-signup-link"
                className="px-4 sm:px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-slate-950 leading-tight" style={{ fontFamily: 'Chivo' }}>
              Professional Weather Verification for Legal & Insurance
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              Retrieve accurate historical weather conditions with full source transparency and audit-grade reporting. Built for contractors, insurance adjusters, and legal professionals.
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/register"
                data-testid="hero-get-started-btn"
                className="px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                Get Started
                <ArrowRight size={16} weight="bold" />
              </Link>
              <a
                href="#features"
                className="px-6 py-3 bg-slate-100 text-slate-900 hover:bg-slate-200 border border-slate-200 transition-colors text-sm font-medium"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold tracking-tight text-slate-950 mb-16" style={{ fontFamily: 'Chivo' }}>Why WeatherProof?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-slate-200">
              <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6">
                <ShieldCheck size={24} weight="bold" className="text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-950 mb-3" style={{ fontFamily: 'Chivo' }}>Source Transparency</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Every report includes station ID, coordinates, distance from location, and data source metadata. No black boxes.
              </p>
            </div>
            <div className="bg-white p-8 border border-slate-200">
              <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6">
                <FileText size={24} weight="bold" className="text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-950 mb-3" style={{ fontFamily: 'Chivo' }}>PDF Reports</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Generate downloadable reports with unique IDs, timestamps, and complete weather data for legal documentation.
              </p>
            </div>
            <div className="bg-white p-8 border border-slate-200">
              <div className="w-12 h-12 bg-blue-50 border border-blue-200 flex items-center justify-center mb-6">
                <Users size={24} weight="bold" className="text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-950 mb-3" style={{ fontFamily: 'Chivo' }}>Built for Professionals</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Designed for insurance, contractors, and legal teams. Clean interface, reproducible results, audit-ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold tracking-tight text-slate-950 mb-8 text-center" style={{ fontFamily: 'Chivo' }}>Unlock Official Weather Reports</h2>
          
          <div className="max-w-2xl mx-auto">
            {/* Free Lookups */}
            <div className="bg-slate-50 border border-slate-200 p-8 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-6 bg-emerald-600 flex items-center justify-center">
                  <span className="text-white text-sm font-bold">✔</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-950" style={{ fontFamily: 'Chivo' }}>Free Lookup Included</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">View historical weather data for any date and location</p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">•</span>
                  <span>Unlimited weather lookups</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">•</span>
                  <span>Full source transparency (stations, coordinates)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">•</span>
                  <span>Save search history</span>
                </li>
              </ul>
            </div>

            {/* Paid Reports */}
            <div className="bg-blue-600 p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-6 bg-white/20 border border-white/40 flex items-center justify-center">
                  <span className="text-white text-lg font-bold">🔒</span>
                </div>
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Chivo' }}>Downloadable Report <span className="text-blue-100 text-sm font-normal">(Paid Feature)</span></h3>
              </div>
              
              <p className="text-blue-50 mb-6 text-sm leading-relaxed">
                Get a clean, timestamped report you can use for:
              </p>
              
              <ul className="space-y-2 text-sm text-blue-50 mb-6">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>Insurance claims</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>Construction delays</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>Property disputes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>Agricultural records</span>
                </li>
              </ul>

              <div className="pt-6 border-t border-blue-400/30">
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-blue-100 mb-2">💰 Pricing</p>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-4xl font-bold" style={{ fontFamily: 'Chivo' }}>$9.99</span>
                    <span className="text-blue-100">per report</span>
                  </div>
                  <ul className="space-y-1 text-sm text-blue-50">
                    <li>• Instant download (PDF)</li>
                    <li>• Unlimited lookups included</li>
                    <li>• Unique report ID for verification</li>
                  </ul>
                </div>

                <Link
                  to="/register"
                  data-testid="pricing-report-btn"
                  className="block w-full text-center px-6 py-3 bg-white text-blue-600 hover:bg-blue-50 transition-colors text-sm font-medium"
                >
                  Get Started – Try Free Lookups
                </Link>
                <p className="text-xs text-blue-100 mt-3 text-center">Secure payment powered by Stripe</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CloudRain size={24} weight="bold" className="text-blue-600" />
                <span className="text-lg font-bold text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
              </div>
              <p className="text-xs text-slate-600">Professional weather verification for legal, insurance, and contractor use.</p>
            </div>
            <div>
              <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-xs text-slate-600 hover:text-slate-950">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-xs text-slate-600 hover:text-slate-950">Privacy Policy</Link></li>
                <li><Link to="/verify-report" className="text-xs text-slate-600 hover:text-slate-950">Verify Report</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-xs text-slate-600 hover:text-slate-950">Features</a></li>
                <li><a href="#features" className="text-xs text-slate-600 hover:text-slate-950">Pricing</a></li>
                <li><Link to="/login" className="text-xs text-slate-600 hover:text-slate-950">Login</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-slate-200 flex items-center justify-between">
            <p className="text-xs text-slate-500">&copy; 2026 WeatherProof. All rights reserved.</p>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-500 italic leading-relaxed">
              <strong>DISCLAIMER:</strong> This report is generated using historical weather data from third-party data providers. 
              While data is sourced from reputable services, accuracy is not guaranteed. This report is provided for informational 
              and documentation purposes only. Acceptance by courts, insurance companies, municipalities, or other entities is not 
              guaranteed. The user is responsible for verifying the suitability of this report for their specific use case.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
